import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

// UI
import { Card } from '@/components/Elements'
import { Checkbox, DatePicker, DummyInput, Input, Select, Textarea } from '@/components/Form'
import { Box, Button, Flex, Grid, GridItem, SimpleGrid } from '@chakra-ui/react'

//Hooks

//Helper
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { ContributorStatus, OfferPaymentCreditTerm, OfferStatus } from '@/features/offers/constants'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { showSOPQuestionnaireModal } from '@/features/offers/slices/offerModal.slice'
import { isNullish } from '@/utils/helper'
import { isNumeric } from '@/utils/stringHelper'

const CommercialCheckListForm = () => {
  // Redux State
  const offerPermissions = useSelector(selectOfferPermissions)

  // Local State
  const [hasMaintenance, setHasMaintenance] = useState(false)
  const [hasProject, setHasProject] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)

  const [isLiabilityCapDisabled, setIsLiabilityCapDisabled] = useState(false)

  // Hooks
  const { getValues, setValue } = useFormContext()
  const { useFetchAllContributorGroups } = useContributorGroup()
  const { data: contributorGroupsResponse } = useFetchAllContributorGroups({
    page: 1,
    pageSize: 0,
    filters: { status: 'active' },
  })

  const bonds = useFieldArray({
    name: 'commercialChecklist.bonds',
  })

  const warranties = useFieldArray({
    name: 'commercialChecklist.warranties',
  })
  const { append: addContributor } = useFieldArray({ name: 'contributors' })

  const subSystems = useWatch({ name: 'subSystems' })
  const commercialChecklist = useWatch({ name: 'commercialChecklist' })
  const retentionPercentage = useWatch({ name: 'commercialChecklist.paymentRetention.value' })
  const isSOP = useWatch({ name: 'isSOP' })
  const siemensRoleInTheOffer = useWatch({ name: 'siemensRoleInTheOffer' })
  const offerId = useWatch({ name: '_id' })

  const dispatch = useDispatch()

  //Translated
  const canEditCommercialChecklist =
    offerPermissions[offerQuotationGroupPolicyType.QUOTATION_COMMERCIAL_CHECKLIST]?.edit ?? false

  const handleOpenSOPQuestionnaire = () => {
    dispatch(
      showSOPQuestionnaireModal({
        offerId: offerId,
      })
    )
  }

  const defaultLegalContributorGroup = contributorGroupsResponse?.data.find(
    (group) => group.type === ContributorGroupType.LEGAL
  )
  const defaultQSContributorGroup = contributorGroupsResponse?.data.find(
    (group) => group.type === ContributorGroupType.QS
  )

  const defaultLegalUser = !isNullish(defaultLegalContributorGroup) ? defaultLegalContributorGroup?.defaultUser : null
  const defaultQSUser = !isNullish(defaultQSContributorGroup) ? defaultQSContributorGroup?.defaultUser : null

  //Setting Offer Auto Value
  useEffect(() => {
    setValue('securityOfPayment', isSOP, { shouldDirty: false })
  }, [isSOP, setValue])

  useEffect(() => {
    if (!isSOP) return
    const current = getValues('commercialChecklist.paymentCreditTerm.value')
    if (!current) {
      setValue('commercialChecklist.paymentCreditTerm.value', 'A060', { shouldDirty: true })
    }
  }, [getValues, setValue, isSOP])

  useEffect(() => {
    if (commercialChecklist?.isSiemensStandardTerms && commercialChecklist.isSiemensStandardTerms === 'yes') {
      setValue(`commercialChecklist.totalLiabilityCap.value`, 'Limited - Standard Term')
    }
  }, [commercialChecklist.isSiemensStandardTerms, setValue])
  useEffect(() => {
    if (subSystems) {
      setHasMaintenance(subSystems.filter((subsystem) => subsystem.type === 'maintenance').length > 0)
      setHasProject(subSystems.filter((subsystem) => subsystem.type === 'project').length > 0)

      const totalPrice = subSystems.reduce((systemPrice, subSystem) => {
        const subSystemTotal = subSystem.totalContractPrice?.newContractRows.reduce((sum, row) => {
          return sum + row.totalHKD
        }, 0)

        return systemPrice + subSystemTotal
      }, 0)

      setTotalPrice(totalPrice)
    }
  }, [subSystems])

  useEffect(() => {
    if (siemensRoleInTheOffer === 'mainContract' && totalPrice > 1000000) {
      setValue(`commercialChecklist.openAccountForEPD`, 'yes')
    } else {
      setValue(`commercialChecklist.openAccountForEPD`, 'no')
    }
    if (siemensRoleInTheOffer === 'mainContract' && totalPrice > 3000000) {
      setValue(`commercialChecklist.declareInCIC`, 'yes')
    } else {
      setValue(`commercialChecklist.declareInCIC`, 'no')
    }
  }, [siemensRoleInTheOffer, setValue, totalPrice])

  useEffect(() => {
    if (retentionPercentage === 'na') {
      setValue('commercialChecklist.paymentRetentionRelease.value', 'na', { shouldDirty: true })
    }
  }, [retentionPercentage, setValue])

  // Handler

  const handleIsSiemensStandardTermsChange = (val) => {
    const status = getValues('status')

    if (![OfferStatus.QUOTATION_DRAFT, OfferStatus.QUOTATION_REJECTED].includes(status)) {
      return
    }

    if (val === 'noWithLegalWaiver') {
      setValue('commercialChecklist.totalLiabilityCap.value', 'unlimited', { shouldDirty: true })
      setIsLiabilityCapDisabled(true)
    } else {
      setIsLiabilityCapDisabled(false)
    }

    if (val === 'no') {
      const contributors = getValues('contributors')
      if (!contributors.some((contributorDetail) => contributorDetail.group === ContributorGroupType.LEGAL)) {
        //Add Legal

        const newContributor = {
          group: ContributorGroupType.LEGAL,
          user: {
            _id: defaultLegalUser?._id || '',
            email: defaultLegalUser?.email || '',
          },
          dueDate: undefined,
          status: ContributorStatus.REQUIRED,
          hasEdit: false,
          canEdit: true,
        }
        addContributor(newContributor)
      }
      if (!contributors.some((contributorDetail) => contributorDetail.group === ContributorGroupType.QS)) {
        //Add Legal

        const newContributor = {
          group: ContributorGroupType.QS,
          user: {
            _id: defaultQSUser?._id || '',
            email: defaultQSUser?.email || '',
          },
          dueDate: undefined,
          status: ContributorStatus.REQUIRED,
          hasEdit: false,
          canEdit: true,
        }
        addContributor(newContributor)
      }
    }
  }

  const addBond = () => {
    const newBond = {
      name: '',
      type: '',
      percentage: '',
      amount: '',
      expiryDate: undefined,
    }

    bonds.append(newBond)
  }

  const removeBond = (index) => {
    bonds.remove(index)
  }

  const handleRequireBondChange = (e) => {
    const { target } = e
    const value = target.type === 'checkbox' ? target.checked : target.value
    const { name } = target

    if (name === 'commercialChecklist.requireBond' && value === 'yes' && commercialChecklist.bonds.length === 0) {
      setValue('commercialChecklist.requireBond', 'yes')
      addBond()
    }
  }

  const processBondAmount = (bond) => {
    let amount = 0
    if (bond.percentage?.value === 'other' && isNumeric(bond?.percentage?.other)) {
      amount = (totalPrice * parseFloat(bond?.percentage?.other)) / 100
    } else if (isNumeric(bond?.percentage?.value)) {
      amount = (totalPrice * parseFloat(bond?.percentage?.value)) / 100
    }

    return amount.toFixed(2)
  }

  const addWarranty = () => {
    const newWarranties = {
      type: '',
      provider: '',
      provideTo: '',
      limitationOfLiability: '',
      exclusiveOfIndirectLoss: '',
    }
    warranties.append(newWarranties)
  }

  const removeWarranty = (index) => {
    warranties.remove(index)
  }

  const handleRequireWarrantyChange = (e) => {
    const { target } = e
    const value = target.type === 'checkbox' ? target.checked : target.value
    const { name } = target

    if (
      name === 'commercialChecklist.warrantyToBeSubmitted' &&
      value === 'yes' &&
      commercialChecklist.warranties.length === 0
    ) {
      setValue('commercialChecklist.warrantyToBeSubmitted', 'yes')
      addWarranty()
    }
  }

  if (!commercialChecklist) {
    return <>Loading </>
  }

  return (
    <Flex direction={'column'} gap={4}>
      <Card title="Payment" open>
        <Flex direction={'column'} gap={4}>
          <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px" mb="5">
            <Flex direction="row" alignItems="center">
              <Checkbox name="securityOfPayment" label="Security Of Payment" disabled={true} isRow labelWidth={35} />
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleOpenSOPQuestionnaire}
                // isDisabled={!canEditCommercialChecklist}
              >
                Fill in SOP
              </Button>
            </Flex>
            <Box></Box>
            <Select
              name="commercialChecklist.paymentCreditTerm.value"
              label="Credit Term"
              isRow
              disabled={!canEditCommercialChecklist}
              options={Object.values(OfferPaymentCreditTerm).map((creditTerm) => {
                return {
                  value: creditTerm,
                  label: creditTerm,
                }
              })}
            />
            <Box></Box>

            {hasMaintenance && (
              <Box px="3">
                <Textarea
                  name="commercialChecklist.paymentBillingMilestoneMaintenance"
                  label="Billing Milestone - Maintenance"
                  placeholder=""
                  disabled={!canEditCommercialChecklist}
                  labelWidth={50}
                  rows="5"
                />
              </Box>
            )}

            {hasProject && (
              <>
                <Box px="3">
                  <Textarea
                    name="commercialChecklist.paymentBillingMilestoneProject"
                    label="Billing Milestone - Project"
                    placeholder=""
                    disabled={!canEditCommercialChecklist}
                    labelWidth={50}
                    rows="5"
                  />
                </Box>
                <Box></Box>
                <Select
                  name="commercialChecklist.paymentRetention.value"
                  label="Retention %"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'na', label: 'NA' },
                    { value: '5%', label: '5%' },
                    { value: '10%', label: '10%' },
                    { value: '10%Upto5%OfTheContractPrice', label: '10% up to 5% of the contract price' },
                    { value: 'other', label: 'other' },
                  ]}
                />
                {commercialChecklist.paymentRetention?.value === 'other' ? (
                  <Input
                    name="commercialChecklist.paymentRetention.other"
                    isRow
                    disabled={!canEditCommercialChecklist}
                  />
                ) : (
                  <Box></Box>
                )}

                <Select
                  name="commercialChecklist.paymentRetentionRelease.value"
                  label="Retention Release upon"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'na', label: 'NA' },
                    {
                      value: 'issuanceOfPracticalCompletionCetificate',
                      label: 'Issuance of Practical Completion Certificate',
                    },
                    {
                      value: 'issuanceOfMakingGoodDefectCertificate',
                      label: 'Issuance of Making Good Defect Certificate',
                    },
                    { value: '50OnPCCert50OnDLPCert', label: '50% on PC Cert. + 50% on DLP Cert.' },
                    { value: 'other', label: 'other' },
                  ]}
                />

                {commercialChecklist.paymentRetentionRelease?.value === 'other' ? (
                  <Input
                    name="commercialChecklist.paymentRetentionRelease.other"
                    isRow
                    disabled={!canEditCommercialChecklist}
                  />
                ) : (
                  <Box></Box>
                )}
              </>
            )}
          </SimpleGrid>
        </Flex>
      </Card>

      {hasProject && (
        <Card title="DLP" open>
          <Flex direction={'column'} gap={4}>
            <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px" mb="5">
              <Select
                name="commercialChecklist.DLPPeriod.value"
                label="DLP Period"
                labelWidth={50}
                isRow
                disabled={!canEditCommercialChecklist}
                options={[
                  { value: '12', label: '12 months' },
                  { value: '18', label: '18 months' },
                  { value: '24', label: '24 months' },
                  { value: 'other', label: 'other' },
                ]}
              />
              {commercialChecklist.DLPPeriod?.value === 'other' ? (
                <Input name="commercialChecklist.DLPPeriod.other" isRow disabled={!canEditCommercialChecklist} />
              ) : (
                <Box></Box>
              )}

              <Select
                name="commercialChecklist.DLPCommenceDate.value"
                label="DLP shall commence from the date of"
                labelWidth={50}
                isRow
                disabled={!canEditCommercialChecklist}
                options={[
                  { value: 'equipmentDelivery', label: 'equipment delivery' },
                  { value: 'completionOfWork', label: 'completion of work' },
                  {
                    value: 'isseanceOfPracticalCompletionCertificate',
                    label: 'issuance of Practical Completion Certificate',
                  },
                  {
                    value: 'isseanceOfPracticalCompletionCertificateOfMainContract',
                    label: 'issuance of Practical Completion Certificate of Main Contract',
                  },
                  { value: 'other', label: 'other' },
                ]}
              />
              {commercialChecklist.DLPCommenceDate?.value === 'other' ? (
                <Input name="commercialChecklist.DLPCommenceDate.other" isRow disabled={!canEditCommercialChecklist} />
              ) : (
                <Box></Box>
              )}
              <Select
                name="commercialChecklist.DLPShallExpire"
                label="DLP shall expiry subject to the issuance of DLP/ Make Good Certificate"
                labelWidth={80}
                isRow
                disabled={!canEditCommercialChecklist}
                options={[
                  { value: 'yes', label: 'yes' },
                  { value: 'no', label: 'no' },
                ]}
              />
              {commercialChecklist.DLPShallExpire === 'yes' ? (
                <Flex>
                  <Select
                    name="commercialChecklist.DLPShallExpireCert.value"
                    isRow
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'DLPCertForTheMainContract', label: 'DLP Cert. for the Main Contract' },
                      { value: 'DLPCertForTheContract', label: 'DLP Cert. for the Contract' },
                      { value: 'other', label: 'other' },
                    ]}
                  />
                  {commercialChecklist.DLPShallExpireCert?.value === 'other' && (
                    <Input
                      name="commercialChecklist.DLPShallExpireCert.other"
                      isRow
                      disabled={!canEditCommercialChecklist}
                    />
                  )}
                </Flex>
              ) : (
                <Box></Box>
              )}
            </SimpleGrid>
          </Flex>
        </Card>
      )}

      <Card title="Terms and conditions" open>
        <Flex direction={'column'} gap={4}>
          <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px" mb="5">
            <Select
              name="commercialChecklist.isSiemensStandardTerms"
              label="Siemens Standard Terms"
              isRow
              disabled={!canEditCommercialChecklist}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'noWithLegalWaiver', label: 'No (with legal waiver)' },
              ]}
              extraOnChange={handleIsSiemensStandardTermsChange}
            />
            <Box></Box>

            {hasProject &&
              commercialChecklist.isSiemensStandardTerms &&
              commercialChecklist.isSiemensStandardTerms !== 'yes' && (
                <>
                  <Select
                    name="commercialChecklist.LDForDelay.value"
                    label="LD for delay ($ per day)"
                    isRow
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'yes', label: 'yes' },
                      { value: 'no', label: 'no' },
                    ]}
                  />

                  {commercialChecklist.LDForDelay?.value === 'yes' ? (
                    <>
                      <Input name="commercialChecklist.LDForDelay.other" isRow disabled={!canEditCommercialChecklist} />
                      <Select
                        name="commercialChecklist.LDForDelayCap.value"
                        label="LD cap for delay  (% of Contract Price)"
                        isRow
                        disabled={!canEditCommercialChecklist}
                        options={[
                          { value: 'limited', label: 'LD capped at:' },
                          { value: 'unlimited', label: 'Unlimited' },
                        ]}
                      />
                      {commercialChecklist.LDForDelayCap?.value === 'limited' ? (
                        <Input
                          name="commercialChecklist.LDForDelayCap.other"
                          isRow
                          disabled={!canEditCommercialChecklist}
                        />
                      ) : (
                        <Box></Box>
                      )}
                    </>
                  ) : (
                    <Box></Box>
                  )}

                  <Select
                    name="commercialChecklist.LDForPerformance.value"
                    label="LD for performance"
                    isRow
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'yes', label: 'yes' },
                      { value: 'no', label: 'no' },
                    ]}
                  />
                  {commercialChecklist.LDForPerformance?.value === 'yes' ? (
                    <>
                      <Input
                        name="commercialChecklist.LDForPerformance.other"
                        isRow
                        disabled={!canEditCommercialChecklist}
                      />
                      <Select
                        name="commercialChecklist.LDForPerformanceCap.value"
                        label="LD cap for performance"
                        isRow
                        disabled={!canEditCommercialChecklist}
                        options={[
                          { value: 'limited', label: 'LD capped at:' },
                          { value: 'unlimited', label: 'Unlimited' },
                        ]}
                      />
                      {commercialChecklist.LDForPerformanceCap?.value === 'limited' ? (
                        <Input
                          name="commercialChecklist.LDForPerformanceCap.other"
                          isRow
                          disabled={!canEditCommercialChecklist}
                        />
                      ) : (
                        <Box></Box>
                      )}
                    </>
                  ) : (
                    <Box></Box>
                  )}
                </>
              )}

            {commercialChecklist.isSiemensStandardTerms && commercialChecklist.isSiemensStandardTerms !== 'yes' && (
              <>
                <Select
                  name="commercialChecklist.hasPreviousAgreementBefore"
                  label="Has Previous Agreement Before"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                />
                <Box></Box>

                <Select
                  name="commercialChecklist.liabilityPerEventCap.value"
                  label="Liability per Event Cap"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                />
                {commercialChecklist.liabilityPerEventCap?.value === 'yes' ? (
                  <Input
                    name="commercialChecklist.liabilityPerEventCap.other"
                    isRow
                    disabled={!canEditCommercialChecklist}
                  />
                ) : (
                  <Box></Box>
                )}
                <Select
                  name="commercialChecklist.totalLiabilityCap.value"
                  label="Total Liability Cap"
                  isRow
                  disabled={
                    !canEditCommercialChecklist ||
                    isLiabilityCapDisabled ||
                    commercialChecklist.isSiemensStandardTerms === 'noWithLegalWaiver'
                  }
                  options={[
                    { value: 'limited', label: 'Total Liability capped at ($):' },
                    { value: 'limitedPercentage', label: 'Total Liability capped at (%):' },
                    { value: 'unlimited', label: 'Unlimited' },
                  ]}
                />
                {commercialChecklist.totalLiabilityCap?.value === 'limited' ||
                commercialChecklist.totalLiabilityCap?.value === 'limitedPercentage' ? (
                  <Input
                    type="number"
                    min="0"
                    name="commercialChecklist.totalLiabilityCap.other"
                    isRow
                    disabled={!canEditCommercialChecklist}
                  />
                ) : (
                  <Box></Box>
                )}
                <Select
                  name="commercialChecklist.isExclusiveOfIndirectLoss"
                  label="Exclusion of indirect / consequential loss & damages etc"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'yes', label: 'yes' },
                    { value: 'no', label: 'no' },
                  ]}
                />
                <Box></Box>
                <Select
                  name="commercialChecklist.isExclusiveOfImpliedWarranty"
                  label="Exclusion of implied warranty"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'yes', label: 'yes' },
                    { value: 'no', label: 'no' },
                  ]}
                />
                <Box></Box>
                <Select
                  name="commercialChecklist.isIPRtoUseOnly"
                  label="Standard IPR to use"
                  isRow
                  disabled={!canEditCommercialChecklist}
                  options={[
                    { value: 'yes', label: 'yes' },
                    { value: 'no', label: 'no' },
                  ]}
                />
              </>
            )}
          </SimpleGrid>
        </Flex>
      </Card>

      {commercialChecklist.isSiemensStandardTerms && commercialChecklist.isSiemensStandardTerms !== 'yes' && (
        <Card
          title="Bond / Guarantee"
          rightElement={
            commercialChecklist.requireBond === 'yes' &&
            canEditCommercialChecklist && (
              <Button colorScheme="green" onClick={() => addBond()}>
                Add Bond
              </Button>
            )
          }
        >
          <Select
            name="commercialChecklist.requireBond"
            label="Bond / Guarantee to be submitted"
            isRow
            labelWidth={50}
            disabled={!canEditCommercialChecklist}
            onChange={(e) => handleRequireBondChange(e)}
            options={[
              { value: 'yes', label: 'yes' },
              { value: 'no', label: 'no' },
            ]}
          />
          {commercialChecklist.requireBond === 'yes' && commercialChecklist.bonds.length > 0 && (
            <>
              {bonds.fields.map((bond, index) => (
                <Card
                  key={bond.id}
                  title={`Bond ${index + 1}`}
                  rightElement={
                    <Button colorScheme="red" onClick={() => removeBond(index)}>
                      Remove
                    </Button>
                  }
                >
                  <Flex direction={'column'} gap={4}>
                    <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                      <GridItem colSpan="7">
                        <Select
                          name={`commercialChecklist.bonds.${index}.type`}
                          label="Select type of bond / Guarantee to be submitted"
                          isRow
                          labelWidth={50}
                          disabled={!canEditCommercialChecklist}
                          options={[
                            { value: 'bidBond', label: 'Bid Bond' },
                            { value: 'performanceBond', label: 'Performance Bond' },
                            { value: 'retentionBond', label: 'Retention Bond' },
                            { value: 'advancePaymentBond', label: 'Advance Payment Bond' },
                            { value: 'parentCompanyGuarantee', label: 'Parent Company Guarantee' },
                            { value: 'other', label: 'other' },
                          ]}
                        />
                      </GridItem>
                      {commercialChecklist.bonds[index]?.type === 'other' && (
                        <GridItem colSpan={'4'}>
                          <Input
                            name={`commercialChecklist.bonds.${index}.name`}
                            isRow
                            disabled={!canEditCommercialChecklist}
                          />
                        </GridItem>
                      )}
                    </Grid>
                    <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                      <GridItem colSpan="7">
                        <Select
                          name={`commercialChecklist.bonds.${index}.percentage.value`}
                          label="Bond / Guarantee %"
                          isRow
                          labelWidth={50}
                          disabled={!canEditCommercialChecklist}
                          options={[
                            { value: '5', label: '5% of Contract Price' },
                            { value: '10', label: '10% of Contract Price' },
                            { value: 'other', label: 'other' },
                          ]}
                        />
                      </GridItem>
                      {commercialChecklist.bonds[index]?.percentage?.value === 'other' && (
                        <GridItem colSpan={'4'}>
                          <Input
                            name={`commercialChecklist.bonds.${index}.percentage.other`}
                            isRow
                            disabled={!canEditCommercialChecklist}
                          />
                        </GridItem>
                      )}
                    </Grid>
                    <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                      <GridItem colSpan="7">
                        <DummyInput
                          label="Bond / Guarantee Amount"
                          value={processBondAmount(bond)}
                          isRow
                          labelWidth={50}
                        />
                      </GridItem>
                    </Grid>
                    <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                      <GridItem colSpan="7">
                        <DatePicker
                          name={`commercialChecklist.bonds.${index}.expiryDate`}
                          label="Bond / Guarantee Expiry Date"
                          isRow
                          labelWidth={50}
                          disabled={!canEditCommercialChecklist}
                        />
                      </GridItem>
                    </Grid>
                  </Flex>
                </Card>
              ))}
            </>
          )}
        </Card>
      )}

      {hasProject && commercialChecklist.isSiemensStandardTerms !== 'yes' && (
        <Card
          title="Form of Warranty or related"
          rightElement={
            commercialChecklist.warrantyToBeSubmitted === 'yes' &&
            canEditCommercialChecklist && (
              <Button colorScheme="green" onClick={() => addWarranty()}>
                Add Warranty
              </Button>
            )
          }
        >
          <Select
            name="commercialChecklist.warrantyToBeSubmitted"
            label="Form of Warranty to be submitted to End Customer"
            isRow
            labelWidth={50}
            disabled={!canEditCommercialChecklist}
            onChange={(e) => handleRequireWarrantyChange(e)}
            options={[
              { value: 'yes', label: 'yes' },
              { value: 'no', label: 'no' },
            ]}
          />
          {commercialChecklist.warrantyToBeSubmitted === 'yes' && commercialChecklist.warranties.length > 0 && (
            <>
              {warranties.fields.map((warranty, index) => (
                <Card
                  key={warranty.id}
                  title={`Warranty ${index + 1}`}
                  rightElement={
                    <Button colorScheme="red" onClick={() => removeWarranty(index)}>
                      Remove
                    </Button>
                  }
                >
                  <Flex direction={'column'} gap={4}>
                    <Input
                      name={`commercialChecklist.warranties.${index}.type`}
                      label="Pls specify type of Warranty / Certificate"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                    />
                    <Input
                      name={`commercialChecklist.warranties.${index}.provider`}
                      label="Provided by:"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                    />
                    <Input
                      name={`commercialChecklist.warranties.${index}.provideTo`}
                      label="Provided to:"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                    />
                    <Input
                      name={`commercialChecklist.warranties.${index}.limitationOfLiability`}
                      label="Limitation of liability:"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                    />
                    <Select
                      name={`commercialChecklist.warranties.${index}.exclusiveOfIndirectLoss`}
                      label="Exclusive of indirect / consequential loss & damages etc"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                      options={[
                        { value: 'yes', label: 'yes' },
                        { value: 'no', label: 'no' },
                      ]}
                    />
                  </Flex>
                </Card>
              ))}
            </>
          )}
        </Card>
      )}

      {commercialChecklist.isSiemensStandardTerms && commercialChecklist.isSiemensStandardTerms !== 'yes' && (
        <>
          <Card title="Customer right to terminate the contract for convenience" open>
            <Flex direction={'column'} gap={4}>
              <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                <GridItem colSpan="8">
                  <Select
                    name="commercialChecklist.customerHasRightToTerminateContract"
                    label="Customer has right to terminate the contract for convenience"
                    labelWidth={60}
                    isRow
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'yes', label: 'yes' },
                      { value: 'no', label: 'no' },
                    ]}
                  />
                </GridItem>
              </Grid>
              {commercialChecklist.customerHasRightToTerminateContract === 'yes' && (
                <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                  <GridItem colSpan="8">
                    <Select
                      name="commercialChecklist.customerTerminationNotifyPeriod.value"
                      label="Notify period for termination without cause: "
                      isRow
                      labelWidth={60}
                      disabled={!canEditCommercialChecklist}
                      options={[
                        { value: '30', label: '30 day notice' },
                        { value: '60', label: '60 day notice' },
                        { value: 'other', label: 'other' },
                      ]}
                    />
                  </GridItem>
                  {commercialChecklist.customerTerminationNotifyPeriod?.value === 'other' && (
                    <GridItem colSpan="4">
                      <Input
                        name="commercialChecklist.customerTerminationNotifyPeriod.other"
                        isRow
                        disabled={!canEditCommercialChecklist}
                      />
                    </GridItem>
                  )}
                </Grid>
              )}
            </Flex>
          </Card>
          <Card title="Insurance" open>
            <Flex direction={'column'} gap={4}>
              <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                <GridItem colSpan="6">
                  <Select
                    name="commercialChecklist.employeeInsuranceProvider.value"
                    label="Employee Compensation Insurance to be provided by"
                    isRow
                    labelWidth={50}
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'employer', label: 'Employer' },
                      { value: 'mainContractor', label: 'Main Contractor' },
                      { value: 'siemens', label: 'Siemens' },
                    ]}
                  />
                </GridItem>

                {commercialChecklist.employeeInsuranceProvider?.value === 'siemens' && (
                  <GridItem colSpan="6">
                    <Select
                      name="commercialChecklist.employeeInsuranceProvider.other"
                      label="Joint Name Required"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                      options={[
                        { value: 'yes', label: 'yes' },
                        { value: 'no', label: 'no' },
                      ]}
                    />
                  </GridItem>
                )}
              </Grid>

              <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                <GridItem colSpan="6">
                  <Select
                    name="commercialChecklist.contractorInsuranceProvider.value"
                    label="Contractor All Risk Insurance to be provided by"
                    isRow
                    labelWidth={50}
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'employer', label: 'Employer' },
                      { value: 'mainContractor', label: 'Main Contractor' },
                      { value: 'siemens', label: 'Siemens' },
                    ]}
                  />
                </GridItem>

                {commercialChecklist.contractorInsuranceProvider?.value === 'siemens' && (
                  <GridItem colSpan="6">
                    <Select
                      name="commercialChecklist.contractorInsuranceProvider.other"
                      label="Joint Name Required"
                      isRow
                      labelWidth={50}
                      disabled={!canEditCommercialChecklist}
                      options={[
                        { value: 'yes', label: 'yes' },
                        { value: 'no', label: 'no' },
                      ]}
                    />
                  </GridItem>
                )}
              </Grid>

              <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                <GridItem colSpan="6">
                  <Select
                    name="commercialChecklist.otherInsuranceProvider.value"
                    label="Other insurance to be provided by Siemens (e.g. Professional Indemnity Insurance )"
                    isRow
                    labelWidth={50}
                    disabled={!canEditCommercialChecklist}
                    options={[
                      { value: 'yes', label: 'yes' },
                      { value: 'no', label: 'no' },
                    ]}
                  />
                </GridItem>

                {commercialChecklist.otherInsuranceProvider?.value === 'yes' && (
                  <GridItem colSpan="6">
                    <Input
                      name="commercialChecklist.otherInsuranceProvider.other"
                      isRow
                      disabled={!canEditCommercialChecklist}
                    />
                  </GridItem>
                )}
              </Grid>
            </Flex>
          </Card>
        </>
      )}

      <Card title="Other">
        <Flex direction={'column'} gap={4}>
          <Input
            name="commercialChecklist.openAccountForEPD"
            label="Open account in EPD for construction wastes if we are Main Contractor and Contract Price > HKD1m"
            defaultValue="no"
            isRow
            labelWidth={80}
            disabled
          />
          <Input
            name="commercialChecklist.declareInCIC"
            label="Declare in CIC and PCFB if we are Main Contractor and Contract Price > HKD3m"
            defaultValue="no"
            isRow
            labelWidth={80}
            disabled
          />
          <Select
            name="commercialChecklist.assignmentConsentForSubContract"
            label="Assignment Consent for Sub-contract"
            isRow
            labelWidth={80}
            disabled={!canEditCommercialChecklist}
            options={[
              { value: 'yes', label: 'yes' },
              { value: 'no', label: 'no' },
            ]}
          />
          <Select
            name="commercialChecklist.nonDisclosureAgreement"
            label="Non-Disclosure Agreement"
            isRow
            labelWidth={80}
            disabled={!canEditCommercialChecklist}
            options={[
              { value: 'nonSiemensStandard', label: 'Yes - Non Siemens Standard' },
              { value: 'siemensStandard', label: 'Yes - Siemens Standard' },
              { value: 'no', label: 'no' },
            ]}
          />
        </Flex>
      </Card>
    </Flex>
  )
}

export default CommercialCheckListForm
